import React, { type FC } from 'react';
import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import Heading from '@atlaskit/heading/heading';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { ContentStatePopupBase } from '../../ContentStatePopupBase/ContentStatePopupBase.tsx';

const styles = cssMap({
	body: {
		padding: token('space.200'),
	},
	footer: {
		paddingInline: token('space.200'),
		paddingBlockEnd: token('space.200'),
	},
});

const i18n = defineMessages({
	title: {
		id: 'content-state.contentStateInfoPopup.live-doc-start-approval-popup.title',
		defaultMessage: 'Approvals are not available on live docs',
		description:
			'Title shown in the live doc start approval popup before opening the convert to page dialog.',
	},
	body: {
		id: 'content-state.contentStateInfoPopup.live-doc-start-approval-popup.body',
		defaultMessage: 'Convert this live doc to a page to start an approval.',
		description:
			'Body text shown in the live doc start approval popup before opening the convert to page dialog.',
	},
	back: {
		id: 'content-state.contentStateInfoPopup.live-doc-start-approval-popup.back',
		defaultMessage: 'Back',
		description:
			'Secondary CTA in the live doc start approval popup that returns to the previous view.',
	},
	cta: {
		id: 'content-state.contentStateInfoPopup.live-doc-start-approval-popup.cta',
		defaultMessage: 'Convert to page',
		description:
			'Primary CTA in the live doc start approval popup that opens the existing convert to page dialog.',
	},
});

/**
 * Popup shown when a user clicks "Start approval" on a live doc. Gating and conversion dispatch
 * are handled by the caller.
 */
export const LiveDocStartApprovalPopup = ({
	onBack,
	onConvertToPage,
}: {
	onBack: () => void;
	onConvertToPage: () => void;
}): ReturnType<FC> => {
	const { formatMessage } = useIntl();

	return (
		<ContentStatePopupBase
			footer={
				<Inline
					testId="live-doc-start-approval-popup-footer"
					xcss={styles.footer}
					alignInline="end"
					space="space.100"
				>
					<Button appearance="subtle" onClick={onBack}>
						{formatMessage(i18n.back)}
					</Button>
					<Button appearance="primary" onClick={onConvertToPage}>
						{formatMessage(i18n.cta)}
					</Button>
				</Inline>
			}
		>
			<Stack testId="live-doc-start-approval-popup" xcss={styles.body} space="space.100">
				<Heading as="h2" size="xsmall">
					{formatMessage(i18n.title)}
				</Heading>
				<Text color="color.text.subtle">{formatMessage(i18n.body)}</Text>
			</Stack>
		</ContentStatePopupBase>
	);
};
